import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PhysicalAddressApiService } from '@features/physical-address/services/physical-address-api/physical-address-api.service';
import { PhysicalAddressConfiguration } from '@features/physical-address/types';
import { PHYSICAL_ADDRESS_CONFIG } from '@rd/core/tokens/physical-address.token';
import { DetailPhysicalAddress, SuggestedPhysicalAddress } from '@rd/types';
import {
  AddressFinderDetailAddressResponse,
  AddressFinderSuggestedAddressesResponse,
} from '@rd/types/physical-address.response';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AddressFinderServiceApi implements PhysicalAddressApiService {
  constructor(
    @Inject(PHYSICAL_ADDRESS_CONFIG)
    private pafConfig: PhysicalAddressConfiguration,
    private httpClient: HttpClient,
  ) {}

  fetchSuggestAddresses(query: string): Observable<SuggestedPhysicalAddress[]> {
    const queryUrl = this.buildSuggestedAddressesUrl(query);
    return this.httpClient
      .get<AddressFinderSuggestedAddressesResponse>(queryUrl)
      .pipe(map((response) => this.transformSuggestedAddresses(response)));
  }

  fetchDetailAddress(
    addressId: string,
  ): Observable<DetailPhysicalAddress | null> {
    const queryUrl = this.buildDetailAddressUrl(addressId);
    return this.httpClient
      .get<AddressFinderDetailAddressResponse>(queryUrl)
      .pipe(map((response) => this.transformDetailAddress(response)));
  }

  buildSuggestedAddressesUrl(query: string): string {
    const { queryParams, queryParamOptions, autocompleteEndpoint } =
      this.pafConfig;
    const paramsData = Object.entries(queryParams)
      .filter(([, value]) => !!value)
      .map(([keyName, value]) => `${keyName}=${value}`)
      .join('&');

    return `${autocompleteEndpoint}&${paramsData}&${
      queryParamOptions.queryKeyword
    }=${encodeURIComponent(query)}`;
  }

  buildDetailAddressUrl(addressId: string): string {
    const { queryParams, queryParamOptions, detailEndpoint } = this.pafConfig;
    const paramsData = Object.entries(queryParams)
      .filter(([, value]) => !!value)
      .map(([keyName, value]) => `${keyName}=${value}`)
      .join('&');

    return `${detailEndpoint}&${paramsData}&${
      queryParamOptions.idKeyword
    }=${encodeURIComponent(addressId)}`;
  }

  transformSuggestedAddresses(
    data: AddressFinderSuggestedAddressesResponse,
  ): SuggestedPhysicalAddress[] {
    if (!data.success) {
      return [];
    }

    return data.completions.reduce((records, item) => {
      const { canonical_address_id, full_address, highlighted_full_address } =
        item;

      records.push({
        addressId: canonical_address_id,
        fullAddress: full_address,
        highlightedAddress: highlighted_full_address ?? null,
      });

      return records;
    }, [] as SuggestedPhysicalAddress[]);
  }

  transformDetailAddress(
    data: AddressFinderDetailAddressResponse,
  ): DetailPhysicalAddress | null {
    if (!data.success) {
      return null;
    }

    const {
      canonical_address,
      full_address,
      address_line_1,
      address_line_2,
      state_territory,
      locality_name,
      postcode,
    } = data;
    return {
      fullAddress: canonical_address ?? full_address,
      addressLine1: address_line_1,
      addressLine2: address_line_2 ?? '',
      city: locality_name,
      state: state_territory,
      country: null,
      postalCode: postcode,
      latitude: null,
      longitude: null,
    };
  }
}
